import { SECTION_TEMPLATES } from "./wizardUtils";

/**
 * The picker for the pages a work's section can hold: a grid of cards, each
 * sketching its layout. No dropdowns — a page is a picture, so it is picked as
 * one.
 */

// A sketch is drawn on this box and scaled by CSS; the proportions are A4's.
const SKETCH_WIDTH = 60;
const SKETCH_HEIGHT = 84;

const INK = "#d1d5db";
const IMAGE_INK = "#9ca3af";

/** One line of imaginary text. */
const TextLine = ({ x, y, width, ink = INK }) => (
  <rect x={x} y={y} width={width} height={1.6} rx={0.8} fill={ink} />
);

/** A run of text lines, `count` of them, `gap` apart. */
const TextLines = ({ x, y, width, count, gap = 4 }) =>
  Array.from({ length: count }, (unused, line) => (
    <TextLine
      key={line}
      x={x}
      y={y + line * gap}
      width={line === count - 1 ? width * 0.6 : width}
    />
  ));

/** An image, or the space one will fill. */
const ImageBlock = props => <rect fill={IMAGE_INK} {...props} />;

/**
 * Where the Artwork Hero's image sits, per shape it can be set as. Every shape
 * spans the content width, margin to margin: a landscape image hangs from a
 * fixed line high on the page with clear paper below it, while a portrait or
 * square one stands on the bottom margin instead, as tall as its proportions
 * make it or as tall as the room below the words, whichever is the less — the
 * width is the one thing it keeps, and a portrait too tall for the page is
 * cropped evenly top and bottom to hold it. With no shape set yet, as in the
 * template picker where no image has been chosen, the image simply fills the
 * space under the words.
 */
export const DETAILS_IMAGE_BLOCKS = {
  landscape: { x: 8, y: 23, width: 44, height: 26 },
  portrait: { x: 8, y: 26, width: 44, height: 50 },
  square: { x: 8, y: 32, width: 44, height: 44 },
};

const DETAILS_IMAGE_UNSET = { x: 8, y: 34, width: 44, height: 42 };

const detailsSketch = imageOrientation => (
  <>
    <TextLines x={8} y={10} width={18} count={2} />
    <TextLines x={34} y={10} width={18} count={4} />
    <ImageBlock
      {...(DETAILS_IMAGE_BLOCKS[imageOrientation] || DETAILS_IMAGE_UNSET)}
    />
  </>
);

// Every sketch but the Artwork Hero's, which draws differently depending on the
// shape its image is set as (see `detailsSketch`).
const TEMPLATE_SKETCHES = {
  landscapes: (
    <>
      <ImageBlock x={8} y={14} width={44} height={26} />
      <ImageBlock x={8} y={44} width={44} height={26} />
    </>
  ),
  // The cells sit closer to each other than to the edge of the page, as they do
  // in print.
  fourDetails: (
    <>
      <ImageBlock x={8} y={8} width={20.5} height={32.5} />
      <ImageBlock x={31.5} y={8} width={20.5} height={32.5} />
      <ImageBlock x={8} y={43.5} width={20.5} height={32.5} />
      <ImageBlock x={31.5} y={43.5} width={20.5} height={32.5} />
    </>
  ),
  fullBleed: <ImageBlock x={0} y={0} width={60} height={84} />,
  biography: (
    <>
      <TextLine x={8} y={12} width={26} ink={IMAGE_INK} />
      <TextLines x={8} y={22} width={44} count={11} />
    </>
  ),
  publicCollections: (
    <>
      <TextLine x={8} y={12} width={30} ink={IMAGE_INK} />
      <TextLines x={8} y={22} width={19} count={9} />
      <TextLines x={33} y={22} width={19} count={9} />
    </>
  ),
  freeText: (
    <>
      <TextLine x={8} y={10} width={24} ink={IMAGE_INK} />
      <TextLine x={8} y={15} width={16} />
      <ImageBlock x={8} y={22} width={44} height={26} />
      <TextLines x={8} y={54} width={44} count={6} />
    </>
  ),
};

/**
 * What each template gives you, for the templates whose own definition has no
 * description (the image-led ones, where the sketch does most of the talking).
 */
const TEMPLATE_HINTS = {
  details: "The catalogue details beside the artist's name, image below.",
  landscapes: "Two landscape images stacked on one page.",
  fourDetails:
    "Four images in a grid. A landscape one shows its central third.",
  fullBleed: "One image filling the page, edge to edge.",
};

/**
 * A single A4-proportioned sketch of a template's layout. `imageOrientation`
 * only means anything to the Artwork Hero, whose image moves with it.
 */
export const TemplateSketch = ({
  template,
  imageOrientation = null,
  className = "h-16 w-12",
}) => (
  <svg
    viewBox={`0 0 ${SKETCH_WIDTH} ${SKETCH_HEIGHT}`}
    className={`${className} shrink-0 border border-gray-200 bg-white`}
    role="presentation"
  >
    {template === "details"
      ? detailsSketch(imageOrientation)
      : TEMPLATE_SKETCHES[template] || null}
  </svg>
);

/**
 * The grid of pages that can be added to a work's section. Picking one adds it
 * straight away, where the user asked for it; its images are then chosen on the
 * page's own row.
 */
export const BrochureTemplateCards = ({ onPick, disabled = false }) => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
    {SECTION_TEMPLATES.map(template => (
      <button
        key={template.id}
        type="button"
        disabled={disabled}
        onClick={() => onPick(template.id)}
        className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left transition hover:border-blue-500 hover:bg-blue-50 disabled:opacity-50"
      >
        <TemplateSketch template={template.id} />
        <span className="min-w-0">
          <span className="block text-sm font-medium">{template.label}</span>
          <span className="mt-0.5 block text-xs text-gray-500">
            {TEMPLATE_HINTS[template.id] || template.description}
          </span>
        </span>
      </button>
    ))}
  </div>
);

export default BrochureTemplateCards;
